import { Injectable, inject } from '@angular/core';
import { CongresoPorUrl } from '@core/models/congreso-por-url.model';
import { CongresoIndice } from '../models/indice.model';
import { ApiService } from '@core/services/api.service';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { CongresoExtendido } from '../models/congreso.model';
import { CacheService } from '@core/services/cache.service';
import { IdiomasCongreso } from '../models/idiomas-congreso.model';

@Injectable({
   providedIn: 'root'
})
export class IndiceService {
   private api = inject(ApiService);
   private cache = inject(CacheService);
   private readonly CACHE_KEY = 'congresos_listado';

   /**
    * Lista los indices de congresos. El endpoint pertenece a `Indice/*`,
    * que es global y no requiere sgiindice.
    */
   getCongresosIndice(): Observable<CongresoIndice[]> {
      return this.cache.get<CongresoIndice[]>(this.CACHE_KEY, {
         usePersistentStorage: true,
         durationMs: 10 * 60 * 1000,
      }).pipe(
         switchMap(cached => {
            if (cached) {
               return of(cached);
            }

            return this.api.post<CongresoIndice[]>('Indice/buscar', {
               estado: '',
               quebusco: ''
            }).pipe(
               tap(data => {
                  this.cache.set(this.CACHE_KEY, data, {
                     usePersistentStorage: true,
                     durationMs: 10 * 60 * 1000,
                  });
               })
            );
         })
      );
   }

   /**
    * Crea o actualiza un indice de congreso. El payload del formulario incluye
    * los campos visibles (congreso, nombre, urls...) y los `bd_*` que viajan
    * de forma transparente al usuario.
    */
   grabarCongresoIndice(datos: any): Observable<any> {
      return this.api.post('Indice/grabar', {
         id: datos.id || '00',
         congreso: datos.congreso,
         nombre: datos.nombre,
         nombrecorto: datos.nombrecorto,
         estado: datos.estado,
         bd_host: datos.bd_host ?? '',
         bd_port: datos.bd_port ?? '',
         bd_nombre: datos.bd_nombre ?? '',
         bd_user: datos.bd_user ?? '',
         bd_pass: datos.bd_pass ?? '',
         url1: datos.url1 || '',
         url2: datos.url2 || '',
         url3: datos.url3 || '',
         url4: datos.url4 || '',
         url5: datos.url5 || '',
      }).pipe(
         tap(() => this.cache.invalidate(this.CACHE_KEY))
      );
   }

   /**
    * Modifica el estado de un congreso índice.
    */
   modificarEstadoCongreso(id: string, estado: string): Observable<any> {
      return this.api.post('Indice/cambiarestado', {
         id,
         estado
      }).pipe(
         tap(() => this.cache.invalidate(this.CACHE_KEY))
      );
   }

   /**
    * Borra un indice de congreso por su id.
    */
   borrarCongresoIndice(id: string): Observable<any> {
      return this.api.post('Indice/borrar', { id }).pipe(
         tap(() => this.cache.invalidate(this.CACHE_KEY))
      );
   }

   /**
    * Busca un indice de congreso por su codigo de congreso.
    */
   getCongresoById(id: string): Observable<any> {
      return this.api.post('Indice/congreso', { congreso: id });
   }

   getIdiomasCongreso(id: string | number): Observable<IdiomasCongreso> {
      return this.api.post<IdiomasCongreso>('Indice/idiomas', { id });
   }

   guardarIdiomasCongreso(id: string | number, idiomas: string[]): Observable<IdiomasCongreso> {
      return this.api.post<IdiomasCongreso>('Indice/idiomas', {
         id,
         idiomas: JSON.stringify(idiomas),
      }).pipe(
         map(datos => this.comprobarIdiomasGuardados(datos, id, idiomas)),
         tap(() => this.cache.invalidate(this.CACHE_KEY)),
         // Una respuesta de escritura no basta: comprobamos una lectura independiente.
         switchMap(() => this.getIdiomasCongreso(id)),
         map(datos => this.comprobarIdiomasGuardados(datos, id, idiomas)),
      );
   }

   private comprobarIdiomasGuardados(datos: IdiomasCongreso, id: string | number, idiomas: string[]): IdiomasCongreso {
      if (
         !datos || Number(datos.id) !== Number(id) || !Array.isArray(datos.idiomas) ||
         JSON.stringify(datos.idiomas) !== JSON.stringify(idiomas)
      ) {
         throw new Error('El servidor no ha confirmado los idiomas seleccionados para este congreso. No se ha completado el guardado.');
      }
      return datos;
   }

   /**
    * Resuelve el congreso asociado a una URL/host publico.
    * El backend devuelve el registro de sgi_indice sin las credenciales de BD de NEO.
    *
    * `ApiService.post` ya desempaqueta el campo `data` de la respuesta, asi
    * que aqui llega directamente el objeto CongresoPorUrl (o null si no hay match).
    */
   resolveCongresoByUrl(
      url: string,
      devolverconfig: boolean = false
   ): Observable<CongresoPorUrl | null> {
      return this.api
         .post<CongresoPorUrl>('Indice/urlcongreso', {
            url,
            devolverconfig: devolverconfig ? '1' : '0'
         })
         .pipe(map(res => res?.id ? res : null));
   }

   /**
    * Valida en el Paso 1 si el congreso ya existe filtrando por la configuración de BD.
    */
   existeCongresoPorDB(payload: any): Observable<boolean> {
      return this.api.post<boolean>('Indice/ExisteCongresoPorDB', payload);
   }

   /**
    * Guarda de manera definitiva los campos extendidos y configuración SMTP del Paso 3.
    */
   grabarCongreso(
      payload: CongresoExtendido,
      sgiindice: string | number
   ): Observable<any> {
      return this.api.post<any>('Congresos/grabar', {
         ...payload,
         sgiindice: String(sgiindice).trim(),
      });
   }

   /**
    * Lista todos los congresos de la BD asociada a un `sgiindice` (cada uno con
    * sus fechas `Comienzo`/`Termino`). Una sola llamada por BD resuelve las
    * fechas de todo el listado del indice. El `sgiindice` viaja en el body para
    * que el interceptor no lo sobreescriba con el global.
    */
   listarCongresosPorIndice(sgiindice: string | number): Observable<any> {
      return this.api.post('Congresos/listar', {
         sgiindice,
      });
   }
}
